import { useUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import { CircularProgress, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { useBulkReceiveSkuSearchLazyQuery } from "./__generated__/bulk-receive-sku-typeahead.gql";

/**
 * Server-backed SKU typeahead for the bulk-receive preview rows.
 *
 * Mirrors `SkuSearchInput`'s `inventorySearchTmp` query but is self-contained
 * (callback-driven, not react-hook-form bound) so it can be embedded per-row
 * in the preview table.
 */
gql`
  query bulkReceiveSkuSearch($name: String, $facilityId: String!) {
    inventorySearchTmp(
      input: { name: $name, facilityId: $facilityId, active: true }
      options: { pageSize: 50, order: { by: NAME_RELAVANCE } }
    ) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

interface SkuOption {
  id: string;
  name: string;
}

interface Props {
  onSelect: (sku: SkuOption | null) => void;
  value?: { skuId: string; name: string } | null;
  "data-testid"?: string;
}

export const BulkReceiveSkuTypeahead: FC<Props> = ({
  onSelect,
  value,
  "data-testid": dataTestId = "bulk-receive-sku-typeahead",
}) => {
  const { facilityId } = useUser();
  const { formatMessage } = useIntl();
  const [options, setOptions] = useState<SkuOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchSkus] = useBulkReceiveSkuSearchLazyQuery({
    fetchPolicy: "no-cache",
  });

  const selected: SkuOption | null = value
    ? { id: value.skuId, name: value.name }
    : null;

  const search = useDebouncedCallback(async (term: string) => {
    if (!term?.trim()) {
      setOptions([]);
      return;
    }
    setLoading(true);
    const { data } = await searchSkus({
      variables: { name: term.trim(), facilityId },
    });
    setLoading(false);
    setOptions(data?.inventorySearchTmp.edges.map((e) => e.node) ?? []);
  }, 300);

  return (
    <Autocomplete<SkuOption, false, false, false>
      options={
        selected && !options.some((o) => o.id === selected.id)
          ? [selected, ...options]
          : options
      }
      value={selected}
      loading={loading}
      fullWidth
      getOptionLabel={(option) => option.name}
      getOptionSelected={(option, val) => option.id === val.id}
      onInputChange={(_, input) => search(input)}
      onChange={(_, next) => onSelect(next)}
      noOptionsText={formatMessage({
        id: "inventory.bulkReceive.skuTypeahead.noOptions",
        defaultMessage: "No matching products",
      })}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          inputProps={{ ...params.inputProps, "data-testid": dataTestId }}
          placeholder={formatMessage({
            id: "inventory.bulkReceive.skuTypeahead.placeholder",
            defaultMessage: "Search for a product",
          })}
          InputProps={{
            ...params.InputProps,
            type: "search",
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
